import { memo, useCallback, useMemo, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Button } from "@design-system/Button/Button";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { useTransactionLedgerAccount } from "@hooks/useTransactionLedgerAccount";
import { CheckIcon, RevertIcon } from "@icons";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import buttonCountChipStyles from "@design-system/Button/buttonCountChip.module.scss";
import {
    addTransactionFieldReviews,
    bulkRevokeFieldReviews,
} from "@api/transaction.api";
import { getUserReviewedFields } from "@common/schemas/transactionFieldReview.schema";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { isTransactionReviewFieldMarkable } from "./transactionFieldReviewUtils";
import styles from "./TransactionReviewRowCell.module.scss";

/**
 * REVIEW column cell used inside a TransactionReviewTaskBody.
 *
 * - If every `meta.reviewTransactionFields` value is reviewed by the current
 *   user: shows a green checkmark that turns into an undo icon on hover.
 * - If any field is still unreviewed (including uncategorized / empty fields
 *   that cannot be marked): no checkmark. Shows "Review (N)" for remaining
 *   markable fields, or nothing when N is 0.
 */
export const TransactionReviewRowCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const meta = getTransactionsTableMeta(table);
        const rawReviewFields = meta.reviewTransactionFields;
        const reviewFields = useMemo(
            () => rawReviewFields ?? [],
            [rawReviewFields],
        );
        const user = useUser();
        const { error: errorToast } = useToaster();
        const [isLoading, setIsLoading] = useState(false);
        const [isHovered, setIsHovered] = useState(false);

        // Only the current user's reviews drive the cell state: the add and
        // revoke endpoints operate on the current user, so deriving from other
        // users' reviews would show an undo affordance that does nothing.
        const reviewedFieldSet = getUserReviewedFields(
            row.original.fieldReviews,
            user?.id,
        );

        // Resolve the effective cash/accrual ledger accounts the same way
        // LedgerCell and TransactionDetails do, so the CASH_LEDGER /
        // ACCRUAL_LEDGER markability check below can run the same
        // isLedgerAccountReviewEligible guard the backend runs — a blank
        // ledger or an "Uncategorized Income/Expenses" placeholder account
        // must never be counted or offered for Confirm.
        const { account: cashAccount } = useTransactionLedgerAccount(
            row.original,
            {
                includeCurrentAccount: true,
                currentAccountId: row.original.accountId ?? undefined,
                basis: AccountingLedgerBasis.cash,
            },
        );
        const { account: accrualAccount } = useTransactionLedgerAccount(
            row.original,
            {
                includeCurrentAccount: true,
                currentAccountId: row.original.accrualAccountId ?? undefined,
                basis: AccountingLedgerBasis.accruals,
            },
        );
        const ledgerAccounts = useMemo(
            () => ({
                cashAccount: cashAccount
                    ? {
                          role: cashAccount.role ?? null,
                          kickOriginalCode: cashAccount.kickOriginalCode,
                      }
                    : null,
                accrualAccount: accrualAccount
                    ? {
                          role: accrualAccount.role ?? null,
                          kickOriginalCode: accrualAccount.kickOriginalCode,
                      }
                    : null,
            }),
            [cashAccount, accrualAccount],
        );

        const markableReviewFields = useMemo(
            () =>
                reviewFields.filter((field) =>
                    isTransactionReviewFieldMarkable(
                        row.original,
                        field,
                        ledgerAccounts,
                    ),
                ),
            [reviewFields, row.original, ledgerAccounts],
        );

        const allReviewed =
            reviewFields.length > 0 &&
            reviewFields.every((f) => reviewedFieldSet.has(f));
        const unreviewedFieldCount = markableReviewFields.filter(
            (field) => !reviewedFieldSet.has(field),
        ).length;

        const handleReview = useCallback(
            async (e: React.MouseEvent) => {
                e.stopPropagation();
                if (markableReviewFields.length === 0) {
                    return;
                }
                setIsLoading(true);
                try {
                    const reviews = await addTransactionFieldReviews(
                        row.original.id,
                        markableReviewFields,
                    );
                    updateTransactionInCache(row.original.id, (t) => ({
                        ...t,
                        fieldReviews: reviews,
                    }));
                } catch (error) {
                    if (isViewOnlyRefusalError(error)) {
                        return;
                    }
                    errorToast("Failed to mark as reviewed. Please try again.");
                } finally {
                    setIsLoading(false);
                }
            },
            [row.original.id, markableReviewFields, errorToast],
        );

        const handleUnreview = useCallback(
            async (e: React.MouseEvent) => {
                e.stopPropagation();
                if (reviewFields.length === 0) {
                    return;
                }
                setIsLoading(true);
                try {
                    await bulkRevokeFieldReviews({
                        transactionIds: [row.original.id],
                        fields: reviewFields,
                    });
                    const revokedFieldSet = new Set(reviewFields);
                    updateTransactionInCache(row.original.id, (t) => ({
                        ...t,
                        fieldReviews: (t.fieldReviews ?? []).filter(
                            (r) =>
                                !(
                                    r.userId === user?.id &&
                                    revokedFieldSet.has(r.field)
                                ),
                        ),
                    }));
                } catch (error) {
                    if (isViewOnlyRefusalError(error)) {
                        return;
                    }
                    errorToast("Failed to undo review. Please try again.");
                } finally {
                    setIsLoading(false);
                }
            },
            [reviewFields, row.original.id, user?.id, errorToast],
        );

        if (reviewFields.length === 0) {
            return null;
        }

        if (allReviewed) {
            return (
                <Button
                    variant="secondary"
                    size="md"
                    iconOnly
                    aria-label="Undo review"
                    onClick={handleUnreview}
                    disabled={isLoading}
                    className={styles.reviewedButton}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {isHovered ? <RevertIcon /> : <CheckIcon />}
                </Button>
            );
        }

        if (unreviewedFieldCount === 0) {
            return null;
        }

        return (
            <Button
                variant="primary"
                size="md"
                onClick={handleReview}
                disabled={isLoading}
                className={viewTableStyles.showOnHover}
                rightIcon={
                    <span
                        data-slot-box=""
                        className={buttonCountChipStyles.countChip}
                    >
                        {unreviewedFieldCount}
                    </span>
                }
            >
                Confirm
            </Button>
        );
    },
);

TransactionReviewRowCell.displayName = "TransactionReviewRowCell";
